<template>
    <div>
        <v-dialog v-model="error" max-width="400">
            <v-card class="pt-4 pb-2">
                <v-card-text>
                    <div class="text-center">{{ $t('file_download_error') }}</div>
                </v-card-text>
                <v-card-actions class="d-flex justify-space-between">
                    <v-btn
                        :loading="isLoading"
                        :disabled="isLoading"
                        size="small"
                        class="bg-primary"
                        @click.stop="download"
                    >
                        {{ $t('try_again') }}
                    </v-btn>
                    <v-btn class="ml-auto bg-error" size="small" @click.stop="error = !error">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <div v-if="!sendEmailStep" class="d-flex flex-nowrap align-center">
            {{ $t('download') }}:
            <v-tooltip v-for="format in formats" :key="format.format" location="bottom">
                <template v-slot:activator="{props}">
                    <v-icon end color="white" class="ml-3" v-bind="props" @click="downloadResults(format.format)">
                        {{ format.icon }}
                    </v-icon>
                </template>
                <span>{{ format.format }}</span>
            </v-tooltip>
            <div class="ml-auto">
                <span class="mr-2">{{ `${$t('send_to_email')}:` }}</span>
                <v-btn icon="mdi-email" variant="text" size="small" @click="sendEmailStep = true" />
            </div>
        </div>
        <div v-else class="d-flex justify-space-between">
            <div class="flex-grow-1">
                <div class="mb-2 d-flex align-center">
                    {{ `${$t('send_to_email')}:` }}
                    <v-radio-group v-model="selectedFormat" density="compact" hide-details inline>
                        <v-radio
                            v-for="item in formats"
                            :key="item.extension"
                            :label="item.format"
                            :value="item.format"
                            :disabled="isLoading"
                        />
                    </v-radio-group>
                </div>
                <div class="d-flex">
                    <v-text-field
                        v-model="message.to"
                        density="compact"
                        :label="$t('email')"
                        type="email"
                        max-width="300"
                        hide-details
                        clearable
                        class="mr-4"
                    />
                    <v-btn :loading="isLoading" :disabled="isLoading" @click="sendEmail">{{ $t('send') }}</v-btn>
                </div>
            </div>
            <v-btn icon="mdi-arrow-left-bold-circle" variant="text" size="small" @click="sendEmailStep = false" />
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {blobToBase64, downloadData} from '~/utils/helpers'

    const message = () => ({
        subject: 'Search Results',
        from: '',
        to: '',
        attachments: [],
        html: '',
    })

    @Component
    export class DownloadSearchResults extends Vue {
        @Prop({required: true}) productType
        @Prop() searchRequest
        @Prop() city
        @Prop() departurePoint

        downloadFormat = 'PDF'
        error = false
        isLoading = false
        formats = [
            {
                format: 'PDF',
                icon: 'mdi-file-pdf-box',
                extension: 'pdf',
            },
            {
                format: 'Excel',
                icon: 'mdi-file-excel',
                extension: 'xls',
            },
            {
                format: 'Word',
                icon: 'mdi-file-word',
                extension: 'doc',
            },
        ]
        selectedFormat = 'PDF'
        sendEmailStep = false
        message = message()

        downloadResults(format) {
            this.downloadFormat = format
            this.download()
        }

        async download() {
            this.isLoading = true
            const offerKeys = await this.getOfferKeys(),
                fileType = this.downloadFormat

            try {
                const format = this.formats.find(el => el.format === this.downloadFormat)

                let data
                let fileName
                if (this.productType === 'hotel') {
                    data = await this.$api.searchAccommodationResults.get({offerKeys, fileType})
                    fileName = this.$t('downloadSearchResults.hotel', {
                        cityName: this.city.name,
                        startDate: this.$filters.dateShortFormat(this.searchRequest.startDate),
                        endDate: this.$filters.dateShortFormat(this.searchRequest.endDate),
                    })
                } else if (this.productType === 'transfer') {
                    data = await this.$api.searchTransfersResults.get({offerKeys, fileType})
                    fileName = this.$t('downloadSearchResults.transfer', {
                        cityName: this.departurePoint.name,
                        dateTime: this.$filters.dateShortFormat(this.searchRequest.dateTime),
                    })
                }

                downloadData(data, `${fileName}.${format.extension}`)
                this.error = false
            } catch (e) {
                this.error = true
            } finally {
                this.isLoading = false
            }
        }

        async getOfferKeys() {
            let keys
            switch (this.productType) {
                case 'hotel':
                    keys = await this.getAccommodationOfferKeys()
                    break
                case 'transfer':
                    keys = await this.getTransferOfferKeys()
                    break

                default:
                    keys = []
            }
            return keys
        }

        async sendEmail() {
            if (!this.message.to) return
            this.isLoading = true
            if (authStore.person?.email) this.message.from = authStore.person?.email
            const userSign = authStore.person
                ? `${authStore.person.firstName || ''} ${authStore.person.lastName || ''}`
                : capitalizeFirstLetter(this.$t('guest', 1))
            const companyName = authStore.person?.companyName || ''
            const offerKeys = await this.getOfferKeys(),
                fileType = this.selectedFormat

            try {
                let buffer
                let subject
                if (this.productType === 'hotel') {
                    buffer = await this.$api.searchAccommodationResults.get({offerKeys, fileType})

                    subject = this.$t(`downloadSearchResults.${this.productType}`, {
                        cityName: this.city.name,
                        startDate: this.$filters.dateShortFormat(this.searchRequest.startDate),
                        endDate: this.$filters.dateShortFormat(this.searchRequest.endDate),
                    })
                    this.message.subject = subject
                    this.message.html = this.$t('downloadSearchResults.hotel_email_message', {
                        cityName: this.city.name,
                        startDate: this.$filters.dateShortFormat(this.searchRequest.startDate),
                        endDate: this.$filters.dateShortFormat(this.searchRequest.endDate),
                        userSign,
                        companyName,
                    })
                } else if (this.productType === 'transfer') {
                    buffer = await this.$api.searchTransfersResults.get({offerKeys, fileType})

                    subject = this.$t(`downloadSearchResults.${this.productType}`, {
                        cityName: this.departurePoint.name,
                        dateTime: this.$filters.dateShortFormat(this.searchRequest.dateTime),
                    })
                    this.message.subject = subject
                    this.message.html = this.$t('downloadSearchResults.transfer_email_message', {
                        cityName: this.departurePoint.name,
                        dateTime: this.$filters.dateShortFormat(this.searchRequest.dateTime),
                        userSign,
                        companyName,
                    })
                }
                let type = 'application/pdf; charset=utf-8'
                if (this.selectedFormat === 'Word') type = 'application/doc; charset=utf-8'
                if (this.selectedFormat === 'Excel') type = 'application/xls; charset=utf-8'

                const blob = new Blob([buffer], {type})
                const path = await blobToBase64(blob)

                this.message.attachments = [{path, filename: subject}]
                await this.$api.mail.post({message: this.message})

                this.$toast.success(this.$t('success_message.send_email'))
                this.message = message()
            } catch (e) {
                this.$toast.error(this.$t('error_message.send_email_error'))
            } finally {
                this.isLoading = false
            }
        }

        async getAccommodationOfferKeys() {
            let result = []
            const offers = await this.$localForage.getItem('hotels')
            hotelsStore.offersToCompare.forEach(offer => {
                if (!offer.rooms?.length) {
                    const searchOffer = offers.find(el => {
                        return el.hotelCode === offer.hotelCode
                    })
                    const offerKeys = searchOffer.rooms.map(room => {
                        return room.groupedOffers[0].offerKey
                    })
                    result = [...result, ...Array.from(new Set(offerKeys))]
                } else {
                    result = [...result, ...offer.rooms]
                }
            })
            return result
        }

        async getTransferOfferKeys() {
            let keys = []
            const transfers = await this.$localForage.getItem('transfers')
            transfersStore.offersToCompare.forEach(el => {
                if (el.offerKeys?.length) {
                    const searchOffer = transfers.find(({productId}) => productId === el.productId)
                    const offerKeys = searchOffer.offers.map(({offerKey}) => offerKey)

                    keys.push(offerKeys)
                } else {
                    keys.push(el.offersKeys)
                }
            })

            return keys
        }
    }

    export default DownloadSearchResults
</script>
